// ============================================================
// syncEngine.js — Bidirectional sync between IndexedDB and Supabase
// ============================================================
// Strategy: delta sync using updated_at timestamps.
// On each sync, fetch only rows updated since last_sync_at.
// Writes go to local first, then push to Supabase silently.
// ============================================================

import { supabase } from './supabase';
import {
  ldbGetAll, ldbPutBulk, ldbGet, ldbPut, getLastSync, setLastSync, clearLocalDB
} from './localDB';
import { cacheInvalidateAll } from './persistentCache';

// Tables to sync — order matters for foreign key relationships
const SYNC_TABLES = [
  { name: 'payment_accounts',   userCol: 'user_id', softDelete: true  },
  { name: 'credit_cards',       userCol: 'user_id', softDelete: true  },
  { name: 'assets',             userCol: 'user_id', softDelete: true  },
  { name: 'liabilities',        userCol: 'user_id', softDelete: true  },
  { name: 'expense_categories', userCol: 'user_id', softDelete: true  },
  { name: 'income_sources',     userCol: 'user_id', softDelete: true  },
  { name: 'user_settings',      userCol: 'user_id', softDelete: false },
  { name: 'transactions',       userCol: 'user_id', softDelete: true  },
  { name: 'transaction_splits', userCol: 'user_id', softDelete: false },
  { name: 'transaction_receipts', userCol: 'user_id', softDelete: false },
  { name: 'tags',                 userCol: 'user_id', softDelete: false },
  { name: 'transaction_tags',     userCol: 'user_id', softDelete: false },
  { name: 'asset_valuations',     userCol: 'user_id', softDelete: false },
];

let _syncing = false;
let _syncListeners = [];

export function onSyncComplete(fn) {
  _syncListeners.push(fn);
  return () => { _syncListeners = _syncListeners.filter(f => f !== fn); };
}

// Exported so direct-local-write call sites (SYNC-2) can notify other
// mounted views (syncVersion bump in AppContext) without paying for a full
// deltaSync round-trip just to get this one side effect.
export function notifyListeners() {
  _syncListeners.forEach(fn => { try { fn(); } catch {} });
}

// ── Full initial sync (first launch) ─────────────────────────────────────────
// Downloads everything from Supabase into IndexedDB.
// All tables fetch CONCURRENTLY — total time is roughly the slowest single
// table's round-trip, not the sum of all 12 (previously sequential, which on
// a real mobile connection at ~500-800ms/round-trip meant 6-10+ seconds).

export async function fullSync(userId) {
  if (!userId) return;
  console.time('[SyncEngine] fullSync');

  await Promise.all(SYNC_TABLES.map(async (table) => {
    try {
      const { data, error } = await supabase.from(table.name).select('*').eq(table.userCol, userId);
      if (error) { console.warn(`[SyncEngine] fullSync ${table.name}:`, error.message); return; }
      if (data?.length) await ldbPutBulk(table.name, data);
      await setLastSync(table.name, new Date().toISOString());
    } catch (e) {
      console.warn(`[SyncEngine] fullSync ${table.name} exception:`, e.message);
    }
  }));

  await cacheInvalidateAll();
  notifyListeners();
  console.timeEnd('[SyncEngine] fullSync');
}

// ── Delta sync (on app open / after writes) ───────────────────────────────────
// Only fetches rows changed since last sync — very fast.

export async function deltaSync(userId) {
  if (!userId || _syncing) return;
  _syncing = true;
  console.time('[SyncEngine] deltaSync');

  try {
    const results = await Promise.all(SYNC_TABLES.map(async (table) => {
      try {
        const lastSync = await getLastSync(table.name);
        const now = new Date().toISOString();

        let query = supabase.from(table.name).select('*').eq(table.userCol, userId);
        if (lastSync) query = query.gt('updated_at', lastSync);
        query = query.limit(5000);

        const { data, error } = await query;
        if (error) return 0;

        if (data?.length) {
          await ldbPutBulk(table.name, data);
        }
        await setLastSync(table.name, now);
        return data?.length || 0;
      } catch {
        return 0;
      }
    }));

    const totalChangedRows = results.reduce((s, n) => s + n, 0);
    const anyChanges = totalChangedRows > 0;

    if (anyChanges) {
      await cacheInvalidateAll();
      notifyListeners();
      // Track change volume and check if an auto-backup is due —
      // cheap check, only writes a snapshot when thresholds are met
      const { noteDataChange, maybeAutoBackup } = await import('./backupEngine');
      noteDataChange(totalChangedRows);
      maybeAutoBackup(userId).catch(() => {});
    }
  } finally {
    _syncing = false;
    console.timeEnd('[SyncEngine] deltaSync');
  }
}

// ── Push a local write to Supabase ────────────────────────────────────────────
// Called after every local write. Fire-and-forget.

export async function pushToSupabase(table, record, operation = 'upsert') {
  try {
    if (operation === 'upsert') {
      await supabase.from(table).upsert(record, { onConflict: 'id' });
    } else if (operation === 'delete') {
      await supabase.from(table).delete().eq('id', record.id);
    }
  } catch (e) {
    // Silent — local write already succeeded, Supabase will catch up on next sync
    console.warn(`[SyncEngine] push ${table} failed:`, e.message);
  }
}

// ── Check if initial sync has been done ──────────────────────────────────────
// Checks ACTUAL local data presence first, not just a timestamp. This is the
// more robust signal: even if sync_meta were ever missing or cleared for some
// reason, a device that already has local transactions should never be forced
// through another blocking full sync — that's exactly the bug that caused
// "still slow even though IndexedDB already has the data" symptoms.

export async function needsFullSync(userId) {
  try {
    const existing = await ldbGetAll('transactions');
    if (existing.some(t => t.user_id === userId)) return false;
  } catch {}
  const lastSync = await getLastSync('transactions');
  return !lastSync;
}

// ── Clear local DB on logout ──────────────────────────────────────────────────

export async function clearOnLogout() {
  await clearLocalDB();
  await cacheInvalidateAll();
}
